import React, {
  createContext,
  useCallback,
  useContext,
  useMemo,
  useState,
} from "react";

import PropTypes from "prop-types";

// Published by IncomeSurfaceGate instances; consumed by DealSetupForm to
// suppress the "Save settings changes?" leave prompt when the form is in a
// gated state. Saving while the gate is active would persist a stranded
// config (docs configured for a persona with no income surface), so the
// modal prompt is worse than silent navigation.
const IncomeSurfaceGateContext = createContext({
  blockedKeys: new Set(),
  reportBlockedPersonas: () => {},
});

export const useIncomeSurfaceGate = () => useContext(IncomeSurfaceGateContext);

export const IncomeSurfaceGateProvider = ({ children }) => {
  const [blockedBySource, setBlockedBySource] = useState(() => ({}));

  const reportBlockedPersonas = useCallback((sourceOrPersonas, maybePersonas) => {
    const sourceId = Array.isArray(sourceOrPersonas)
      ? "__default__"
      : sourceOrPersonas;
    const personas = Array.isArray(sourceOrPersonas)
      ? sourceOrPersonas
      : (maybePersonas || []);
    const normalized = Array.from(new Set(personas)).sort();

    setBlockedBySource((prev) => {
      const current = prev[sourceId] || [];
      const unchanged =
        current.length === normalized.length &&
        current.every((k, idx) => k === normalized[idx]);
      if (unchanged) return prev;

      const next = { ...prev };
      if (normalized.length === 0) {
        delete next[sourceId];
      } else {
        next[sourceId] = normalized;
      }
      return next;
    });
  }, []);

  const blockedKeys = useMemo(() => {
    const next = new Set();
    Object.values(blockedBySource).forEach((keys) => {
      keys.forEach((key) => next.add(key));
    });
    return next;
  }, [blockedBySource]);

  const value = useMemo(
    () => ({ blockedKeys, reportBlockedPersonas }),
    [blockedKeys, reportBlockedPersonas]
  );

  return (
    <IncomeSurfaceGateContext.Provider value={value}>
      {children}
    </IncomeSurfaceGateContext.Provider>
  );
};

IncomeSurfaceGateProvider.propTypes = {
  children: PropTypes.node.isRequired,
};
